import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Dialog,
  CircularProgress,
  Alert as MuiAlert
} from '@/ui';
import { useNavigate } from 'react-router-dom';
import {
  SpeedIcon,
  CheckCircleIcon,
  SecurityIcon,
  WarningIcon,
  CloseIcon
} from '@/ui';
import BottomTabBar from '@/components/BottomTabBar';
import Header from '@/components/Header';
import { httpClient } from '@/shared/api/http';
import { getCustomerId } from '@/shared/lib/utils/getCustomerId';
import { buildOpenBankingFetchPayload } from '@/shared/lib/openBankingFetchPayload';
import IncomeOnboardingOverlay from '@/components/onboarding/IncomeOnboardingOverlay';
import { OnboardingService } from '@/shared/api/services';
import { useAuth } from '@/shared/state/store';
import {
  INCOME_ONBOARDING_STEP_OFFSET,
  incomeOnboardingSchemas,
} from '@/pages/private/noNavButFooter/UpdateBudgetPageCom';

interface OpenBankingResponse {
  success: boolean;
  bankLink?: string;
  providerLogoUri?: string;
  providerDisplayName?: string;
  status?: string;
  successful?: boolean;
  inProgress?: boolean;
}

interface BenefitItemProps {
  icon: React.ReactNode;
  title: string;
  description: string;
}

const BenefitItem: React.FC<BenefitItemProps> = ({ icon, title, description }) => (
  <Box data-testid="pages-private-from-dashboard-benefit-item-container-action-1" sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
    <Box data-testid="pages-private-from-dashboard-benefit-item-container-action-2"
      sx={{
        backgroundColor: '#f2f7f7',
        borderRadius: '50%',
        width: { xs: 32, md: 37 },
        height: { xs: 32, md: 37 },
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0
      }}
    >
      {icon}
    </Box>
    <Box data-testid="pages-private-from-dashboard-benefit-item-container-action-3" sx={{ flex: 1 }}>
      <Typography data-testid="pages-private-from-dashboard-benefit-item-text-action-1"
        variant="body1"
        sx={{
          fontWeight: 600,
          fontSize: { xs: '16px', md: '16px' },
          color: '#1a202c',
          mb: 0.5,
          lineHeight: 'normal'
        }}
      >
        {title}
      </Typography>
      <Typography data-testid="pages-private-from-dashboard-benefit-item-text-action-2"
        variant="body2"
        sx={{
          fontSize: { xs: '14px', md: '14px' },
          color: '#4a5568',
          lineHeight: 'normal'
        }}
      >
        {description}
      </Typography>
    </Box>
  </Box>
);

const OpenBankingPage: React.FC = () => {
  const navigate = useNavigate();
  const { openBankingOnly, logout, user } = useAuth();
  const [showMobileModal, setShowMobileModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [pendingConnect, setPendingConnect] = useState(false);
  const [shouldRenderOnboarding, setShouldRenderOnboarding] = useState(false);
  const [confirmCancelOpen, setConfirmCancelOpen] = useState(false);
  const [confirmLogoutOpen, setConfirmLogoutOpen] = useState(false);

  const handleOnboardingComplete = () => {
    setShowOnboarding(false);
    if (pendingConnect) {
      setPendingConnect(false);
      void handleConnectBank(true);
    } else {
      setShouldRenderOnboarding(false);
    }
  };

  const handleOnboardingCancel = () => {
    setPendingConnect(false);
    setShowOnboarding(false);
    setShouldRenderOnboarding(false);
  };

  const startOpenBanking = async (customerId: number) => {
    const response = await httpClient.post<OpenBankingResponse>(
      '/iehub-customer/api/v1/openbanking/fetch',
      buildOpenBankingFetchPayload(customerId, '', user?.isPartner)
    );

    if (response.data.success && response.data.bankLink) {
      // Redirect to bank authentication page
      // Bank will redirect back to /ob-confirm-transaction?SearchID=xxx
      window.location.href = response.data.bankLink;
    } else {
      setError('Failed to get bank link. Please try again.');
      setLoading(false);
    }
  };

  const handleConnectBank = async (skipOnboardingCheck = false) => {
    setLoading(true);
    setError(null);

    try {
      const customerId = await getCustomerId();

      if (!customerId) {
        setError('Unable to identify customer. Please try again or contact support.');
        setLoading(false);
        return;
      }

      // Creditor invited "Open Banking-only" users should not see the secondary
      // income onboarding overlay; they are locked to the Open Banking journey.
      const shouldSkipIncomeOnboarding = skipOnboardingCheck || openBankingOnly;

      if (!shouldSkipIncomeOnboarding) {
        const resp = await OnboardingService.getCustomerFieldStage(Number(customerId), {
          skipErrorCapture: true
        });
        const totalSteps = incomeOnboardingSchemas.length;
        const parsed = resp?.fieldStage ? parseInt(resp.fieldStage, 10) : NaN;
        const completed =
          Number.isFinite(parsed) && parsed >= INCOME_ONBOARDING_STEP_OFFSET + totalSteps;

        if (!completed) {
          setPendingConnect(true);
          setShouldRenderOnboarding(true);
          setShowOnboarding(true);
          setLoading(false);
          return;
        }
      }

      await startOpenBanking(Number(customerId));
    } catch (err: any) {
      console.error('Failed to start Open Banking flow', err);
      const errorMessage =
        err.response?.data?.message || 'Failed to connect to Open Banking. Please try again.';
      setError(errorMessage);
      setLoading(false);
    }
  };

  const handleCancel = () => {
    if (openBankingOnly) {
      setConfirmLogoutOpen(true);
      return;
    }
    setConfirmCancelOpen(true);
  };

  const handleConfirmCancel = () => {
    setConfirmCancelOpen(false);
    navigate('/dashboard');
  };

  const handleCancelDialogClose = () => {
    setConfirmCancelOpen(false);
  };

  const handleLogoutDialogClose = () => {
    setConfirmLogoutOpen(false);
  };

  const handleConfirmLogout = async () => {
    setConfirmLogoutOpen(false);
    try {
      // Legacy parity: creditor Open Banking invitation journeys should mark the invite as rejected
      // when the user cancels from the Open Banking start page.
      try {
        if (openBankingOnly) {
          await OnboardingService.rejectOpenBankInvitationIfPresent(user, {
            skipErrorCapture: true,
          });
        }
      } catch (inviteError) {
        // Non-blocking: proceed with logout even if this fails.
        // eslint-disable-next-line no-console
        console.warn('Failed to update Open Banking invitation status (rejected)', inviteError);
      }

      await logout({ source: 'manual' });
    } finally {
      navigate('/auth', { replace: true });
    }
  };

  const handleDecline = () => {
    // TODO: Implement manual entry flow
    console.log('User chose manual entry');
    if (openBankingOnly) return;
    navigate(-1);
  };

  const handleConnectClick = () => {
    void handleConnectBank();
  };

  const benefits = [
    {
      icon: <SpeedIcon sx={{ fontSize: { xs: 14, md: 16 }, color: '#003d40' }} />,
      title: 'Faster completion',
      description: 'No need to manually type in your transactions.'
    },
    {
      icon: <CheckCircleIcon sx={{ fontSize: { xs: 14, md: 16 }, color: '#003d40' }} />,
      title: 'Increased accuracy',
      description: 'Reduce errors by importing data directly.'
    },
    {
      icon: <SecurityIcon sx={{ fontSize: { xs: 14, md: 16 }, color: '#003d40' }} />,
      title: 'Safe & secure',
      description: 'We use bank-level security and will never see your login details.'
    }
  ];

  return (
    <>
      {shouldRenderOnboarding && (
        <IncomeOnboardingOverlay
          open={showOnboarding}
          onComplete={handleOnboardingComplete}
          onCancel={handleOnboardingCancel}
        />
      )}
      {/* Mobile View */}
      <Box data-testid="pages-private-from-dashboard-open-banking-page-container-action-1"
        sx={{
          display: { xs: 'flex', md: 'none' },
          height: '100dvh',
          width: '100vw',
          backgroundColor: '#f9fafa',
          flexDirection: 'column',
          overflow: 'hidden'
        }}
      >
        {/* Header */}
        <Header data-testid="pages-private-from-dashboard-open-banking-page-header-action-1" />

        {/* Mobile Content */}
        <Box data-testid="pages-private-from-dashboard-open-banking-page-container-action-2"
          sx={{
            flex: 1,
            overflow: 'auto',
            p: 3,
            pb: 10
          }}
        >
          {/* Error Alert */}
          {error && (
            <MuiAlert 
              severity="error" 
              onClose={() => setError(null)}
              sx={{ mb: 2 }}
            >
              {error}
            </MuiAlert>
          )}

          <Box data-testid="pages-private-from-dashboard-open-banking-page-container-action-3" sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {/* Title Section */}
            <Box data-testid="pages-private-from-dashboard-open-banking-page-container-action-4">
              <Typography data-testid="pages-private-from-dashboard-open-banking-page-text-connect-with-open-banking-1"
                variant="h5"
                sx={{
                  fontWeight: 700,
                  fontSize: '24px',
                  lineHeight: '32px',
                  color: '#1a202c',
                  mb: 2
                }}
              >
                Connect with open banking
              </Typography>
              <Typography data-testid="pages-private-from-dashboard-open-banking-page-text-action-1"
                variant="body1"
                sx={{
                  fontSize: '16px',
                  lineHeight: '24px',
                  color: '#4a5568'
                }}
              >
                Save time and fill out your budget form faster and more accurately. Securely connect your bank account to automatically import your spending and payments.
              </Typography>
            </Box>

            {/* Benefits Section */}
            <Box data-testid="pages-private-from-dashboard-open-banking-page-container-action-5" sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {benefits.map((benefit, index) => (
                <BenefitItem
                  key={index}
                  icon={benefit.icon}
                  title={benefit.title}
                  description={benefit.description}
                />
              ))}
            </Box>

            {/* Warning Section */}
            {!openBankingOnly &&    <Box data-testid="pages-private-from-dashboard-open-banking-page-container-action-6"
              sx={{
                backgroundColor: '#fff5f5',
                borderLeft: '4px solid #e53e3e',
                borderTopRightRadius: '6px',
                borderBottomRightRadius: '6px',
                p: 2
              }}
            >
              <Box data-testid="pages-private-from-dashboard-open-banking-page-container-action-7" sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
                <WarningIcon sx={{ fontSize: 14, color: '#e53e3e', mt: 0.3 }} />
                <Box data-testid="pages-private-from-dashboard-open-banking-page-container-action-8">
                  <Typography data-testid="pages-private-from-dashboard-open-banking-page-text-please-note:-1"
                    variant="body2"
                    sx={{
                      fontSize: '16px',
                      fontWeight: 600,
                      color: '#000',
                      mb: 0.5
                    }}
                  >
                    Please note:
                  </Typography>
                  <Typography data-testid="pages-private-from-dashboard-open-banking-page-text-action-2"
                    variant="body2"
                    sx={{
                      fontSize: '14px',
                      color: '#000',
                      lineHeight: 'normal'
                    }}
                  >
                    Choosing "yes" will replace any information you've already entered manually with data from your bank.
                  </Typography>
                </Box>
              </Box>
            </Box> }

            {/* Action Buttons */}
            <Box data-testid="pages-private-from-dashboard-open-banking-page-container-action-9" sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mt: 2 }}>
              <Button data-testid="pages-private-from-dashboard-open-banking-page-btn-handle-connect-click-1"
                variant="contained"
                onClick={handleConnectClick}
                disabled={loading}
                sx={{
                  backgroundColor: '#d4e03b',
                  color: '#003d3b',
                  textTransform: 'none',
                  borderRadius: '999px',
                  py: 1.5,
                  fontSize: '16px',
                  fontWeight: 700,
                  boxShadow: 'none',
                  '&:hover': {
                    backgroundColor: '#c5d630',
                    boxShadow: 'none'
                  },
                  '&:disabled': {
                    backgroundColor: '#e0e0e0',
                    color: '#9e9e9e'
                  }
                }}
              >
                {loading ? (
                  <>
                    <CircularProgress size={20} sx={{ mr: 1, color: '#003d3b' }} />
                    Connecting...
                  </>
                ) : (
                  'Yes, connect my bank'
                )}
              </Button>
              <Button data-testid="pages-private-from-dashboard-open-banking-page-btn-handle-cancel-1"
                variant="outlined"
                onClick={handleCancel}
                sx={{
                  color: '#004b49',
                  borderColor: '#cbd5e0',
                  borderWidth: 2,
                  textTransform: 'none',
                  borderRadius: '999px',
                  py: 1.5,
                  fontSize: '16px',
                  fontWeight: 600,
                  '&:hover': {
                    borderColor: '#cbd5e0',
                    borderWidth: 2,
                    backgroundColor: 'rgba(0,0,0,0.02)'
                  }
                }}
              >
                Cancel
              </Button>
            </Box>
          </Box>
        </Box>

        {/* Bottom Tab Bar */}
        <BottomTabBar data-testid="pages-private-from-dashboard-open-banking-page-nav-bar-action" />
      </Box>

      {/* Desktop View */}
      <Box data-testid="pages-private-from-dashboard-open-banking-page-container-action-10"
        sx={{
          height: '100vh',
          width: '100vw',
          display: { xs: 'none', md: 'flex' },
          flexDirection: 'column',
          backgroundColor: '#f5f5f5',
          overflow: 'hidden'
        }}
      >
        {/* Header */}
        <Header data-testid="pages-private-from-dashboard-open-banking-page-header-action-2" />

        {/* Desktop Content - Centered Modal */}
        <Box data-testid="pages-private-from-dashboard-open-banking-page-container-action-11"
          sx={{
            flex: 1,
            overflow: 'auto',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            py: 4
          }}
        >
          <Paper data-testid="pages-private-from-dashboard-open-banking-page-container-action-12"
            sx={{
              maxWidth: '896px',
              width: '100%',
              mx: 2,
              p: 6,
              borderRadius: '16px',
              boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
            }}
          >
            {/* Error Alert */}
            {error && (
              <MuiAlert 
                severity="error" 
                onClose={() => setError(null)}
                sx={{ mb: 3 }}
              >
                {error}
              </MuiAlert>
            )}

            <Box data-testid="pages-private-from-dashboard-open-banking-page-container-action-13" sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              {/* Title */}
              <Typography data-testid="pages-private-from-dashboard-open-banking-page-text-connect-with-open-banking-2"
                variant="h4"
                sx={{
                  fontWeight: 400,
                  fontSize: '36px',
                  lineHeight: '40px',
                  color: '#1a202c'
                }}
              >
                Connect with open banking
              </Typography>

              {/* Description */}
              <Typography data-testid="pages-private-from-dashboard-open-banking-page-text-action-3"
                variant="body1"
                sx={{
                  fontSize: '18px',
                  lineHeight: '28px',
                  color: '#4a5568'
                }}
              >
                Save time and fill out your budget form faster and more accurately. Securely connect your bank account to automatically import your spending and payments.
              </Typography>

              {/* Benefits Section */}
              <Box data-testid="pages-private-from-dashboard-open-banking-page-container-action-14" sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {benefits.map((benefit, index) => (
                  <BenefitItem
                    key={index}
                    icon={benefit.icon}
                    title={benefit.title}
                    description={benefit.description}
                  />
                ))}
              </Box>

              {/* Warning Section */}
              {!openBankingOnly &&    <Box data-testid="pages-private-from-dashboard-open-banking-page-container-action-15"
                sx={{
                  backgroundColor: '#fff5f5',
                  borderLeft: '4px solid #e53e3e',
                  borderTopRightRadius: '6px',
                  borderBottomRightRadius: '6px',
                  p: 2.5
                }}
              >
                <Box data-testid="pages-private-from-dashboard-open-banking-page-container-action-16" sx={{ display: 'flex', gap: 3, alignItems: 'flex-start' }}>
                  <WarningIcon sx={{ fontSize: 16, color: '#e53e3e', mt: 0.5 }} />
                  <Box data-testid="pages-private-from-dashboard-open-banking-page-container-action-17" sx={{ flex: 1 }}>
                    <Typography data-testid="pages-private-from-dashboard-open-banking-page-text-please-note:-2"
                      variant="body2"
                      sx={{
                        fontSize: '16px',
                        fontWeight: 600,
                        color: '#000',
                        mb: 0.5,
                        lineHeight: '24px'
                      }}
                    >
                      Please note:
                    </Typography>
                    <Typography data-testid="pages-private-from-dashboard-open-banking-page-text-action-4"
                      variant="body2"
                      sx={{
                        fontSize: '14px',
                        color: '#000',
                        lineHeight: '20px'
                      }}
                    >
                      Choosing "yes" will replace any information you've already entered manually with data from your bank.
                    </Typography>
                  </Box>
                </Box>
              </Box> }

              {/* Action Buttons */}
              <Box data-testid="pages-private-from-dashboard-open-banking-page-container-action-18" sx={{ display: 'flex', gap: 2, mt: 1 }}>
                <Button data-testid="pages-private-from-dashboard-open-banking-page-btn-handle-cancel-2"
                  variant="outlined"
                  onClick={handleCancel}
                  sx={{
                    color: '#004b49',
                    borderColor: '#cbd5e0',
                    borderWidth: 2,
                    textTransform: 'none',
                    borderRadius: '999px',
                    px: 3.5,
                    py: 1.75,
                    fontSize: '16px',
                    fontWeight: 600,
                    '&:hover': {
                      borderColor: '#cbd5e0',
                      borderWidth: 2,
                      backgroundColor: 'rgba(0,0,0,0.02)'
                    }
                  }}
                >
                  Cancel
                </Button>
                <Button data-testid="pages-private-from-dashboard-open-banking-page-btn-handle-connect-click-2"
                  variant="contained"
                onClick={handleConnectClick}
                  disabled={loading}
                  sx={{
                    backgroundColor: '#d4e03b',
                    color: '#003d3b',
                    textTransform: 'none',
                    borderRadius: '999px',
                    px: 5.5,
                    py: 1.75,
                    fontSize: '16px',
                    fontWeight: 600,
                    boxShadow: 'none',
                    '&:hover': {
                      backgroundColor: '#c5d630',
                      boxShadow: 'none'
                    },
                    '&:disabled': {
                      backgroundColor: '#e0e0e0',
                      color: '#9e9e9e'
                    }
                  }}
                >
                  {loading ? (
                    <>
                      <CircularProgress size={20} sx={{ mr: 1, color: '#003d3b' }} />
                      Connecting...
                    </>
                  ) : (
                    'Yes, connect my bank'
                  )}
                </Button>
              </Box>
            </Box>
          </Paper>
        </Box>
      </Box>

      {/* Mobile Modal Version (Alternative Design) */}
      <Dialog data-testid="pages-private-from-dashboard-open-banking-page-modal-action-1"
        open={showMobileModal}
        onClose={() => setShowMobileModal(false)}
        fullScreen
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiDialog-paper': {
            backgroundColor: 'white',
            borderTopLeftRadius: '12px',
            borderTopRightRadius: '12px'
          }
        }}
      >
        <Box data-testid="pages-private-from-dashboard-open-banking-page-container-action-19" sx={{ p: 2 }}>
          {/* Header with Close Button */}
          <Box data-testid="pages-private-from-dashboard-open-banking-page-container-action-20"
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              mb: 3
            }}
          >
            <Typography data-testid="pages-private-from-dashboard-open-banking-page-text-open-banking"
              variant="h6"
              sx={{
                fontWeight: 600,
                fontSize: '18px',
                color: '#000'
              }}
            >
              Open Banking
            </Typography>
            <CloseIcon
              onClick={() => setShowMobileModal(false)}
              sx={{ fontSize: 14, cursor: 'pointer' }}
            />
          </Box>

          {/* Icon */}
          <Box data-testid="pages-private-from-dashboard-open-banking-page-container-action-21"
            sx={{
              width: 68,
              height: 68,
              borderRadius: '50%',
              backgroundColor: 'rgba(212, 233, 57, 0.2)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mb: 3
            }}
          >
            <Box data-testid="pages-private-from-dashboard-open-banking-page-container-action-22"
              sx={{
                width: 47.6,
                height: 47.6,
                borderRadius: '50%',
                backgroundColor: '#d4e939',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <SecurityIcon sx={{ fontSize: 25.5, color: '#003d40' }} />
            </Box>
          </Box>

          {/* Title and Description */}
          <Typography data-testid="pages-private-from-dashboard-open-banking-page-text-speed-up-with-open-banking"
            variant="h5"
            sx={{
              fontWeight: 700,
              fontSize: '30px',
              lineHeight: '36px',
              color: '#003d40',
              mb: 2.5
            }}
          >
            Speed up with Open Banking
          </Typography>
          <Typography data-testid="pages-private-from-dashboard-open-banking-page-text-action-5"
            variant="body1"
            sx={{
              fontSize: '16px',
              lineHeight: '24px',
              color: '#6b7280',
              mb: 3.5
            }}
          >
            Connect your bank account to automatically fill in your spending and payments. It's fast, secure, and saves you from typing everything yourself.
          </Typography>

          {/* Steps */}
          <Box data-testid="pages-private-from-dashboard-open-banking-page-container-action-23"
            sx={{
              backgroundColor: '#f5f5f5',
              borderRadius: '8px',
              p: 2,
              mb: 3,
              display: 'flex',
              flexDirection: 'column',
              gap: 2
            }}
          >
            <Typography data-testid="pages-private-from-dashboard-open-banking-page-text-step-1:-click-continue-with-open-banking"
              variant="body2"
              sx={{
                fontSize: '16px',
                fontWeight: 600,
                color: '#111827'
              }}
            >
              Step 1: Click "Continue with Open Banking"
            </Typography>
            <Typography data-testid="pages-private-from-dashboard-open-banking-page-text-step-2:-connect-your-bank-account-securely"
              variant="body2"
              sx={{
                fontSize: '16px',
                fontWeight: 600,
                color: '#111827'
              }}
            >
              Step 2: Connect your bank account securely
            </Typography>
            <Typography data-testid="pages-private-from-dashboard-open-banking-page-text-step-3:-review-and-submit-your-details"
              variant="body2"
              sx={{
                fontSize: '16px',
                fontWeight: 600,
                color: '#111827'
              }}
            >
              Step 3: Review and Submit your details
            </Typography>
          </Box>

          {/* Warning */}
          {!openBankingOnly &&    <Box data-testid="pages-private-from-dashboard-open-banking-page-container-action-24"
            sx={{
              backgroundColor: '#fef2f2',
              borderLeft: '4px solid #dc2626',
              borderRadius: '6px',
              p: 2,
              mb: 3
            }}
          >
            <Box data-testid="pages-private-from-dashboard-open-banking-page-container-action-25" sx={{ display: 'flex', gap: 1.5 }}>
              <WarningIcon sx={{ fontSize: 16, color: '#dc2626', mt: 0.5 }} />
              <Typography data-testid="pages-private-from-dashboard-open-banking-page-text-action-6"
                variant="body2"
                sx={{
                  fontSize: '14px',
                  color: '#000',
                  lineHeight: '20px'
                }}
              >
                <Box data-testid="pages-private-from-dashboard-open-banking-page-container-please-note:" component="span" sx={{ fontWeight: 700 }}>
                  Please note:
                </Box>{' '}
                Connecting your bank will replace any information you've already typed in manually.
              </Typography>
            </Box>
          </Box> }

          {/* Question */}
          <Typography data-testid="pages-private-from-dashboard-open-banking-page-text-how-would-you-like-to-proceed?"
            variant="h6"
            sx={{
              fontSize: '20px',
              fontWeight: 600,
              color: '#111827',
              textAlign: 'center',
              mb: 3
            }}
          >
            How would you like to proceed?
          </Typography>

          {/* Buttons */}
          <Box data-testid="pages-private-from-dashboard-open-banking-page-container-action-26"
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
              pt: 3,
              borderTop: '1px solid #e5e7eb'
            }}
          >
            <Button data-testid="pages-private-from-dashboard-open-banking-page-btn-handle-connect-click-3"
              variant="contained"
                onClick={handleConnectClick}
              disabled={loading}
              sx={{
                backgroundColor: '#d4e939',
                color: '#003d40',
                textTransform: 'none',
                borderRadius: '12px',
                py: 1.75,
                fontSize: '18px',
                fontWeight: 700,
                boxShadow: 'none',
                '&:hover': {
                  backgroundColor: '#c5d630',
                  boxShadow: 'none'
                },
                '&:disabled': {
                  backgroundColor: '#e0e0e0',
                  color: '#9e9e9e'
                }
              }}
            >
              {loading ? (
                <>
                  <CircularProgress size={20} sx={{ mr: 1, color: '#003d40' }} />
                  Connecting...
                </>
              ) : (
                'Yes, use Open Banking'
              )}
            </Button>
            {!openBankingOnly && (
              <Button data-testid="pages-private-from-dashboard-open-banking-page-btn-handle-decline"
                variant="text"
                onClick={handleDecline}
                sx={{
                  color: '#6b7280',
                  textTransform: 'none',
                  borderRadius: '12px',
                  py: 1.75,
                  fontSize: '16px',
                  fontWeight: 600,
                  '&:hover': {
                    backgroundColor: 'rgba(0,0,0,0.02)'
                  }
                }}
              >
                No, I'll fill it out myself
              </Button>
            )}
          </Box>
        </Box>
      </Dialog>

      <Dialog data-testid="pages-private-from-dashboard-open-banking-page-modal-action-2"
        open={confirmCancelOpen}
        onClose={handleCancelDialogClose}
        maxWidth="xs"
        fullWidth
      >
        <Box data-testid="pages-private-from-dashboard-open-banking-page-container-action-27" sx={{ p: 3 }}>
          <Typography data-testid="pages-private-from-dashboard-open-banking-page-text-cancel-open-banking?"
            variant="h6"
            sx={{ fontWeight: 600, mb: 1.5 }}
          >
            Cancel Open Banking?
          </Typography>
          <Typography data-testid="pages-private-from-dashboard-open-banking-page-text-action-7"
            variant="body2"
            sx={{ mb: 3 }}
          >
            Are you sure you want to cancel the Open Banking journey and return to your dashboard?
          </Typography>
          <Box data-testid="pages-private-from-dashboard-open-banking-page-container-action-28" sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1.5 }}>
            <Button data-testid="pages-private-from-dashboard-open-banking-page-btn-handle-cancel-dialog-close"
              variant="outlined"
              onClick={handleCancelDialogClose}
              sx={{ textTransform: 'none' }}
            >
              Stay on this page
            </Button>
            <Button data-testid="pages-private-from-dashboard-open-banking-page-btn-handle-confirm-cancel"
              variant="contained"
              onClick={handleConfirmCancel}
              sx={{ textTransform: 'none' }}
            >
              Yes, cancel
            </Button>
          </Box>
        </Box>
      </Dialog>

      <Dialog data-testid="pages-private-from-dashboard-open-banking-page-modal-action-3"
        open={confirmLogoutOpen}
        onClose={handleLogoutDialogClose}
        maxWidth="xs"
        fullWidth
      >
        <Box data-testid="pages-private-from-dashboard-open-banking-page-container-action-29" sx={{ p: 3 }}>
          <Typography data-testid="pages-private-from-dashboard-open-banking-page-text-logout?" variant="h6" sx={{ fontWeight: 600, mb: 1.5 }}>
            Logout?
          </Typography>
          <Typography data-testid="pages-private-from-dashboard-open-banking-page-text-action-8" variant="body2" sx={{ mb: 3 }}>
            Are you sure you want to logout? You will need to sign in again to continue.
          </Typography>
          <Box data-testid="pages-private-from-dashboard-open-banking-page-container-action-30" sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1.5 }}>
            <Button data-testid="pages-private-from-dashboard-open-banking-page-btn-handle-logout-dialog-close" variant="outlined" onClick={handleLogoutDialogClose} sx={{ textTransform: 'none' }}>
              Stay signed in
            </Button>
            <Button data-testid="pages-private-from-dashboard-open-banking-page-btn-handle-confirm-logout"
              variant="contained"
              color="error"
              onClick={handleConfirmLogout}
              sx={{ textTransform: 'none' }}
            >
              Logout
            </Button>
          </Box>
        </Box>
      </Dialog>
    </>
  );
};

export default OpenBankingPage;

